import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface User {
  id: number;
  name: string;
  email?: string;
  isGuest: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, phone?: string) => Promise<void>;
  guestCheckout: (name: string, email?: string, phone?: string) => Promise<void>;
  logout: () => Promise<void>;
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = '/api/auth';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const getToken = () => localStorage.getItem('sessionToken');
  const setToken = (token: string) => localStorage.setItem('sessionToken', token);
  const removeToken = () => localStorage.removeItem('sessionToken');

  const validateSession = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/session`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setUser(data.user);
        } else {
          removeToken();
        }
      } else {
        removeToken();
      }
    } catch {
      console.error('Session validation failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  const login = async (email: string, password: string) => {
    const response = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    setToken(data.sessionToken);
    setUser(data.user);
    setShowAuthModal(false);
  };

  const register = async (name: string, email: string, password: string, phone?: string) => {
    const response = await fetch(`${API_BASE}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, phone, isGuest: false }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Registration failed');
    }

    setToken(data.sessionToken);
    setUser(data.user);
    setShowAuthModal(false);
  };

  const guestCheckout = async (name: string, email?: string, phone?: string) => {
    if (!email && !phone) {
      throw new Error('Email or phone number is required');
    }

    const response = await fetch(`${API_BASE}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, phone, isGuest: true }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Guest checkout failed');
    }

    setToken(data.sessionToken);
    setUser(data.user);
    setShowAuthModal(false);
  };

  const logout = async () => {
    const token = getToken();
    if (token) {
      try {
        await fetch(`${API_BASE}/session`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        console.error('Logout request failed');
      }
    }
    removeToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        guestCheckout,
        logout,
        showAuthModal,
        setShowAuthModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
